/**
 * POSManager - Point of Sale for event merchandise
 * Sell books, caps, t-shirts, and other items
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import api from '@/lib/api';

// UI Components
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';

// Icons
import {
  ShoppingCart, Plus, Minus, Trash2, Package, DollarSign, CreditCard,
  Banknote, Search, BarChart3, AlertTriangle, Loader2, Receipt,
  BookOpen, Shirt, Gift, Coffee, Disc, Edit, RefreshCw, X, Check
} from 'lucide-react';

const CATEGORY_ICONS = {
  books: BookOpen,
  apparel: Shirt,
  accessories: Gift,
  food: Coffee,
  media: Disc,
  other: Package
};

const PAYMENT_METHODS = [
  { id: 'cash', name: 'Cash', icon: Banknote },
  { id: 'card', name: 'Card', icon: CreditCard },
  { id: 'mobile', name: 'Mobile Pay', icon: DollarSign },
];

// ============== Product Card ==============
const ProductCard = ({ product, onAddToCart, cartQty }) => {
  const CategoryIcon = CATEGORY_ICONS[product.category] || Package;
  const isOutOfStock = product.quantity <= 0;
  const isLowStock = product.quantity > 0 && product.quantity <= product.low_stock_threshold;

  return (
    <Card 
      className={`cursor-pointer transition-all hover:shadow-md ${isOutOfStock ? 'opacity-50' : ''} ${cartQty > 0 ? 'ring-2 ring-primary' : ''}`}
      onClick={() => !isOutOfStock && onAddToCart(product)}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={`p-3 rounded-lg ${isOutOfStock ? 'bg-slate-100' : 'bg-primary/10'}`}>
            <CategoryIcon className={`h-6 w-6 ${isOutOfStock ? 'text-slate-400' : 'text-primary'}`} />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-medium truncate">{product.name}</h4>
            <p className="text-xs text-muted-foreground truncate">{product.sku}</p>
            <div className="flex items-center justify-between mt-2">
              <span className="text-lg font-bold">${product.price.toFixed(2)}</span>
              <div className="flex items-center gap-2">
                {isLowStock && (
                  <Badge variant="outline" className="text-amber-600 border-amber-300">
                    Low Stock
                  </Badge>
                )}
                {isOutOfStock ? (
                  <Badge variant="secondary">Out of Stock</Badge>
                ) : (
                  <Badge variant="outline">{product.quantity} left</Badge>
                )}
              </div>
            </div>
            {cartQty > 0 && (
              <div className="mt-2 flex items-center gap-2">
                <Badge className="bg-primary">{cartQty} in cart</Badge>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ============== Cart Item ==============
const CartItem = ({ item, onUpdateQty, onRemove }) => {
  return (
    <div className="flex items-center justify-between py-2 border-b last:border-0">
      <div className="flex-1 min-w-0 pr-2">
        <p className="font-medium text-sm truncate">{item.name}</p>
        <p className="text-xs text-muted-foreground">${item.price.toFixed(2)} each</p>
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="icon"
          className="h-7 w-7"
          onClick={() => onUpdateQty(item.product_id, item.quantity - 1)}
        >
          <Minus className="h-3 w-3" />
        </Button>
        <span className="w-8 text-center font-medium">{item.quantity}</span>
        <Button
          variant="outline"
          size="icon"
          className="h-7 w-7"
          onClick={() => onUpdateQty(item.product_id, item.quantity + 1)}
          disabled={item.quantity >= item.stock}
        >
          <Plus className="h-3 w-3" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-destructive"
          onClick={() => onRemove(item.product_id)}
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
      <div className="w-20 text-right font-medium">
        ${(item.price * item.quantity).toFixed(2)}
      </div>
    </div>
  );
};

// ============== Add/Edit Product Dialog ==============
const ProductDialog = ({ open, onClose, eventId, product }) => {
  const queryClient = useQueryClient();
  const isEditing = !!product;

  const [form, setForm] = useState({
    name: product?.name || '',
    description: product?.description || '',
    category: product?.category || 'other',
    sku: product?.sku || '',
    price: product?.price || 0,
    cost: product?.cost || 0,
    quantity: product?.quantity || 0,
    low_stock_threshold: product?.low_stock_threshold || 5,
    image_url: product?.image_url || '',
    is_active: product?.is_active ?? true
  });

  const createMutation = useMutation({
    mutationFn: (data) => api.post(`/admin/events/${eventId}/pos/products`, data),
    onSuccess: () => {
      toast.success('Product created');
      queryClient.invalidateQueries(['pos-products', eventId]);
      onClose();
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to create product')
  });

  const updateMutation = useMutation({
    mutationFn: (data) => api.put(`/admin/events/${eventId}/pos/products/${product.id}`, data),
    onSuccess: () => {
      toast.success('Product updated');
      queryClient.invalidateQueries(['pos-products', eventId]);
      onClose();
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to update product')
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isEditing) {
      updateMutation.mutate(form);
    } else {
      createMutation.mutate(form);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Product' : 'Add New Product'}</DialogTitle>
          <DialogDescription>
            {isEditing ? 'Update product details' : 'Add a new item to your store'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-2">
              <Label>Product Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g., Event T-Shirt"
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Category *</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="books">Books</SelectItem>
                  <SelectItem value="apparel">Apparel</SelectItem>
                  <SelectItem value="accessories">Accessories</SelectItem>
                  <SelectItem value="food">Food & Beverages</SelectItem>
                  <SelectItem value="media">Media (CDs/DVDs)</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>SKU (auto-generated if empty)</Label>
              <Input
                value={form.sku}
                onChange={(e) => setForm({ ...form, sku: e.target.value })}
                placeholder="SKU-001"
              />
            </div>

            <div className="space-y-2">
              <Label>Sale Price *</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="number"
                  step="0.01"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })}
                  className="pl-10"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Cost Price (optional)</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="number"
                  step="0.01"
                  value={form.cost}
                  onChange={(e) => setForm({ ...form, cost: parseFloat(e.target.value) || 0 })}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Initial Stock</Label>
              <Input
                type="number"
                value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: parseInt(e.target.value) || 0 })}
              />
            </div>

            <div className="space-y-2">
              <Label>Low Stock Alert</Label>
              <Input
                type="number"
                value={form.low_stock_threshold}
                onChange={(e) => setForm({ ...form, low_stock_threshold: parseInt(e.target.value) || 5 })}
              />
            </div>

            <div className="col-span-2 space-y-2">
              <Label>Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Product description..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
              {(createMutation.isPending || updateMutation.isPending) ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Check className="h-4 w-4 mr-2" />
              )}
              {isEditing ? 'Save Changes' : 'Add Product'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ============== Checkout Dialog ==============
const CheckoutDialog = ({ open, onClose, cart, total, eventId, onSuccess }) => {
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [amountTendered, setAmountTendered] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [notes, setNotes] = useState('');
  const [discountAmount, setDiscountAmount] = useState(0);

  const saleMutation = useMutation({
    mutationFn: (data) => api.post(`/admin/events/${eventId}/pos/sales`, data),
    onSuccess: (response) => {
      toast.success(
        <div>
          <p className="font-semibold">Sale Complete!</p>
          <p className="text-sm">Receipt: {response.data.receipt_number}</p>
          {response.data.change > 0 && (
            <p className="text-sm text-green-600">Change: ${response.data.change.toFixed(2)}</p>
          )}
        </div>,
        { duration: 5000 }
      );
      onSuccess();
      onClose();
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Sale failed')
  });

  const finalTotal = total - discountAmount;
  const change = paymentMethod === 'cash' && amountTendered 
    ? parseFloat(amountTendered) - finalTotal 
    : 0;

  const handleCheckout = () => {
    saleMutation.mutate({
      items: cart.map(item => ({
        product_id: item.product_id,
        quantity: item.quantity,
        unit_price: item.price
      })),
      payment_method: paymentMethod,
      amount_tendered: paymentMethod === 'cash' ? parseFloat(amountTendered) || finalTotal : null,
      customer_name: customerName || null,
      discount_amount: discountAmount,
      notes: notes || null
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5" />
            Checkout
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Order Summary */}
          <div className="p-3 bg-slate-50 rounded-lg space-y-2">
            <div className="flex justify-between text-sm">
              <span>Subtotal ({cart.length} items)</span>
              <span>${total.toFixed(2)}</span>
            </div>
            {discountAmount > 0 && (
              <div className="flex justify-between text-sm text-green-600">
                <span>Discount</span>
                <span>-${discountAmount.toFixed(2)}</span>
              </div>
            )}
            <Separator />
            <div className="flex justify-between font-bold text-lg">
              <span>Total</span>
              <span>${finalTotal.toFixed(2)}</span>
            </div>
          </div>

          {/* Payment Method */}
          <div className="space-y-2">
            <Label>Payment Method</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {PAYMENT_METHODS.map((method) => (
                <Button
                  key={method.id}
                  type="button"
                  variant={paymentMethod === method.id ? 'default' : 'outline'}
                  className="flex-col h-16"
                  onClick={() => setPaymentMethod(method.id)}
                >
                  <method.icon className="h-5 w-5 mb-1" />
                  <span className="text-xs">{method.name}</span>
                </Button>
              ))}
            </div>
          </div>

          {/* Cash Payment */}
          {paymentMethod === 'cash' && (
            <div className="space-y-2">
              <Label>Amount Tendered</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="number"
                  step="0.01"
                  value={amountTendered}
                  onChange={(e) => setAmountTendered(e.target.value)}
                  placeholder={finalTotal.toFixed(2)}
                  className="pl-10"
                />
              </div>
              {change > 0 && (
                <div className="p-2 bg-green-50 rounded text-green-700 text-center font-bold">
                  Change Due: ${change.toFixed(2)}
                </div>
              )}
              {change < 0 && amountTendered && (
                <div className="p-2 bg-red-50 rounded text-red-700 text-center text-sm">
                  Insufficient: need ${Math.abs(change).toFixed(2)} more
                </div>
              )}
            </div>
          )}

          {/* Discount */}
          <div className="space-y-2">
            <Label>Discount (optional)</Label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="number"
                step="0.01"
                value={discountAmount || ''}
                onChange={(e) => setDiscountAmount(parseFloat(e.target.value) || 0)}
                placeholder="0.00"
                className="pl-10"
              />
            </div>
          </div>

          {/* Customer Name */}
          <div className="space-y-2">
            <Label>Customer Name (optional)</Label>
            <Input
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              placeholder="For receipt"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button 
            onClick={handleCheckout}
            disabled={saleMutation.isPending || (paymentMethod === 'cash' && change < 0)}
            className="bg-green-600 hover:bg-green-700"
          >
            {saleMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Check className="h-4 w-4 mr-2" />
            )}
            Complete Sale
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============== Main POS Manager ==============
const POSManager = ({ eventId }) => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('pos');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [cart, setCart] = useState([]);
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showCheckout, setShowCheckout] = useState(false);

  // Fetch products
  const { data: products, isLoading: productsLoading } = useQuery({
    queryKey: ['pos-products', eventId, categoryFilter, searchQuery],
    queryFn: () => api.get(`/admin/events/${eventId}/pos/products`, {
      params: {
        category: categoryFilter !== 'all' ? categoryFilter : undefined,
        search: searchQuery || undefined
      }
    }).then(r => r.data),
    enabled: !!eventId
  });

  // Fetch stats
  const { data: stats } = useQuery({
    queryKey: ['pos-stats', eventId],
    queryFn: () => api.get(`/admin/events/${eventId}/pos/stats`).then(r => r.data),
    enabled: !!eventId && activeTab === 'stats'
  });

  // Fetch sales history
  const { data: salesData } = useQuery({
    queryKey: ['pos-sales', eventId],
    queryFn: () => api.get(`/admin/events/${eventId}/pos/sales`).then(r => r.data),
    enabled: !!eventId && activeTab === 'history'
  });

  // Cart functions
  const addToCart = (product) => {
    setCart(prev => {
      const existing = prev.find(item => item.product_id === product.id);
      if (existing) {
        if (existing.quantity >= product.quantity) {
          toast.error('Not enough stock');
          return prev;
        }
        return prev.map(item =>
          item.product_id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, {
        product_id: product.id,
        name: product.name,
        price: product.price,
        quantity: 1,
        stock: product.quantity
      }];
    });
  };

  const updateCartQty = (productId, newQty) => {
    if (newQty <= 0) {
      setCart(prev => prev.filter(item => item.product_id !== productId));
    } else {
      setCart(prev => prev.map(item =>
        item.product_id === productId
          ? { ...item, quantity: newQty }
          : item
      ));
    }
  };

  const removeFromCart = (productId) => {
    setCart(prev => prev.filter(item => item.product_id !== productId));
  };

  const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  const handleSaleComplete = () => {
    setCart([]);
    queryClient.invalidateQueries(['pos-products', eventId]);
    queryClient.invalidateQueries(['pos-stats', eventId]);
    queryClient.invalidateQueries(['pos-sales', eventId]);
  };

  if (productsLoading && activeTab === 'pos') {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-48" />
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <ShoppingCart className="h-5 w-5 text-primary" />
            Point of Sale
          </h2>
          <p className="text-sm text-muted-foreground">
            Sell merchandise, books, and other items
          </p>
        </div>
        <Button onClick={() => { setEditingProduct(null); setShowProductDialog(true); }}>
          <Plus className="h-4 w-4 mr-2" />
          Add Product
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="pos" className="gap-2">
            <ShoppingCart className="h-4 w-4" />
            POS Terminal
          </TabsTrigger>
          <TabsTrigger value="products" className="gap-2">
            <Package className="h-4 w-4" />
            Products
          </TabsTrigger>
          <TabsTrigger value="history" className="gap-2">
            <Receipt className="h-4 w-4" />
            Sales History
          </TabsTrigger>
          <TabsTrigger value="stats" className="gap-2">
            <BarChart3 className="h-4 w-4" />
            Statistics
          </TabsTrigger>
        </TabsList>

        {/* POS Terminal */}
        <TabsContent value="pos" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Products Grid */}
            <div className="lg:col-span-2 space-y-4">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search products..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    <SelectItem value="books">Books</SelectItem>
                    <SelectItem value="apparel">Apparel</SelectItem>
                    <SelectItem value="accessories">Accessories</SelectItem>
                    <SelectItem value="food">Food</SelectItem>
                    <SelectItem value="media">Media</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {!products || products.length === 0 ? (
                <Card className="p-8 text-center">
                  <Package className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                  <h3 className="font-semibold text-lg mb-2">No Products Yet</h3>
                  <p className="text-muted-foreground mb-4">
                    Add products to start selling at your event
                  </p>
                  <Button onClick={() => { setEditingProduct(null); setShowProductDialog(true); }}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add First Product
                  </Button>
                </Card>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {products.map((product) => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      onAddToCart={addToCart}
                      cartQty={cart.find(c => c.product_id === product.id)?.quantity || 0}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Cart */}
            <Card className="h-fit sticky top-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <ShoppingCart className="h-5 w-5" />
                  Cart
                  {cartItemCount > 0 && (
                    <Badge>{cartItemCount}</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {cart.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <ShoppingCart className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Cart is empty</p>
                    <p className="text-xs">Click products to add</p>
                  </div>
                ) : (
                  <>
                    <ScrollArea className="h-64">
                      <div className="space-y-1">
                        {cart.map((item) => (
                          <CartItem
                            key={item.product_id}
                            item={item}
                            onUpdateQty={updateCartQty}
                            onRemove={removeFromCart}
                          />
                        ))}
                      </div>
                    </ScrollArea>
                    <Separator className="my-4" />
                    <div className="space-y-2">
                      <div className="flex justify-between text-lg font-bold">
                        <span>Total</span>
                        <span>${cartTotal.toFixed(2)}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          variant="outline"
                          onClick={() => setCart([])}
                        >
                          <X className="h-4 w-4 mr-2" />
                          Clear
                        </Button>
                        <Button
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => setShowCheckout(true)}
                        >
                          <Receipt className="h-4 w-4 mr-2" />
                          Checkout
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Products Management */}
        <TabsContent value="products" className="mt-4">
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead>SKU</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Stock</TableHead>
                  <TableHead>Sold</TableHead>
                  <TableHead>Revenue</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products?.map((product) => {
                  const CategoryIcon = CATEGORY_ICONS[product.category] || Package;
                  const isLowStock = product.quantity <= product.low_stock_threshold;
                  return (
                    <TableRow key={product.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <CategoryIcon className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">{product.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{product.sku}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">{product.category}</Badge>
                      </TableCell>
                      <TableCell>${product.price.toFixed(2)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {isLowStock && <AlertTriangle className="h-4 w-4 text-amber-500" />}
                          <span className={isLowStock ? 'text-amber-600 font-medium' : ''}>
                            {product.quantity}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>{product.total_sold || 0}</TableCell>
                      <TableCell>${(product.total_revenue || 0).toFixed(2)}</TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setEditingProduct(product); setShowProductDialog(true); }}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {(!products || products.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No products found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Sales History */}
        <TabsContent value="history" className="mt-4">
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Receipt #</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead>Payment</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Cashier</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {salesData?.sales?.map((sale) => (
                  <TableRow key={sale.id}>
                    <TableCell className="font-mono text-sm">{sale.receipt_number}</TableCell>
                    <TableCell>{new Date(sale.created_at).toLocaleString()}</TableCell>
                    <TableCell>{sale.items?.length || 0} items</TableCell>
                    <TableCell>
                      <Badge variant="outline">{sale.payment_method}</Badge>
                    </TableCell>
                    <TableCell className="font-medium">${sale.total.toFixed(2)}</TableCell>
                    <TableCell>{sale.cashier_name}</TableCell>
                    <TableCell>
                      <Badge variant={sale.status === 'completed' ? 'default' : 'destructive'}>
                        {sale.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
                {(!salesData?.sales || salesData.sales.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No sales yet
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Statistics */}
        <TabsContent value="stats" className="mt-4">
          {stats ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <Card className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Receipt className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{stats.sales.count}</p>
                      <p className="text-xs text-muted-foreground">Total Sales</p>
                    </div>
                  </div>
                </Card>
                <Card className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <DollarSign className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">${stats.sales.revenue.toFixed(2)}</p>
                      <p className="text-xs text-muted-foreground">Revenue</p>
                    </div>
                  </div>
                </Card>
                <Card className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Package className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{stats.sales.items_sold}</p>
                      <p className="text-xs text-muted-foreground">Items Sold</p>
                    </div>
                  </div>
                </Card>
                <Card className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-100 rounded-lg">
                      <BarChart3 className="h-5 w-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">${stats.sales.average_sale.toFixed(2)}</p>
                      <p className="text-xs text-muted-foreground">Avg. Sale</p>
                    </div>
                  </div>
                </Card>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Top Products */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Top Selling Products</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {stats.top_products.length > 0 ? (
                      <div className="space-y-2">
                        {stats.top_products.slice(0, 5).map((product, idx) => (
                          <div key={product.id} className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-muted-foreground w-4">{idx + 1}.</span>
                              <span>{product.name}</span>
                            </div>
                            <div className="text-sm">
                              <span className="text-muted-foreground">{product.sold} sold</span>
                              <span className="ml-2 font-medium">${product.revenue.toFixed(2)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-center py-4">No data yet</p>
                    )}
                  </CardContent>
                </Card>

                {/* Low Stock Alerts */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-500" />
                      Low Stock Alerts
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {stats.low_stock_alerts.length > 0 ? (
                      <div className="space-y-2">
                        {stats.low_stock_alerts.map((product) => (
                          <div key={product.id} className="flex items-center justify-between p-2 bg-amber-50 rounded">
                            <span>{product.name}</span>
                            <Badge variant="outline" className="text-amber-600">
                              {product.quantity} left
                            </Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-center py-4">All products well stocked</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
              <p className="text-muted-foreground">Loading statistics...</p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <ProductDialog
        open={showProductDialog}
        onClose={() => { setShowProductDialog(false); setEditingProduct(null); }}
        eventId={eventId}
        product={editingProduct}
      />

      <CheckoutDialog
        open={showCheckout}
        onClose={() => setShowCheckout(false)}
        cart={cart}
        total={cartTotal}
        eventId={eventId}
        onSuccess={handleSaleComplete}
      />
    </div>
  );
};

export default POSManager;
